import * as Sentry from '@sentry/react';
import { ApiError } from '@core/api/apiClient';

/**
 * True when the ApiError represents a client-side transport failure that
 * cannot be acted on from the app — the browser had no connectivity or
 * the request exceeded its timeout. These are not bugs and reporting
 * them floods the Sentry issue list with noise that recycles forever.
 *
 * The two codes are assigned in `apiClient.ts`: NETWORK_ERROR when axios
 * reports no response object, TIMEOUT_ERROR when the request aborts past
 * the 30s client timeout.
 */
export function isExpectedClientTransportError(error: unknown): boolean {
  return error instanceof ApiError && error.isNetworkError();
}

/**
 * Normalize an endpoint path for stable Sentry grouping.
 *
 * Replaces numeric ids, UUIDs, and long alphanumeric slugs with placeholders so
 * that `/api/reservations/22542407` and `/api/reservations/22542408` share the
 * same fingerprint.
 */
export function normalizeEndpoint(raw?: string): string {
  if (!raw) return 'unknown';
  let path: string;
  try {
    path = new URL(raw, 'http://dummy').pathname;
  } catch {
    path = raw.split('?')[0];
  }
  return path
    .replace(/\/\d+(?=\/|$)/g, '/:id')
    .replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?=\/|$)/gi, '/:uuid')
    .replace(/\/[A-Za-z0-9_-]{20,}(?=\/|$)/g, '/:token');
}

/**
 * Capture API errors with structured context and explicit fingerprint.
 *
 * Fingerprint is [tag, status, code, method, endpoint] so Sentry groups events
 * by the *kind* of failure (e.g. "401 on POST /api/traditional-grab") rather
 * than by stack trace (which would collapse all ApiErrors into a single issue).
 */
export function captureApiError(error: unknown, context?: Record<string, unknown>) {
  if (isExpectedClientTransportError(error)) return;

  if (!(error instanceof ApiError)) {
    const source = typeof context?.source === 'string' ? context.source : undefined;
    Sentry.withScope((scope) => {
      if (source) scope.setTag('source', source);
      if (context) scope.setContext('error_context', context);
      Sentry.captureException(error);
    });
    return;
  }

  const endpoint = normalizeEndpoint(error.endpoint ?? (context?.url as string | undefined));
  const method = error.method ?? (context?.method as string | undefined) ?? 'UNKNOWN';
  const status = error.status ?? 0;

  const source = typeof context?.source === 'string' ? context.source : undefined;

  Sentry.withScope((scope) => {
    scope.setTag('api.status', String(status || 'unknown'));
    scope.setTag('api.code', error.code);
    scope.setTag('api.endpoint', endpoint);
    scope.setTag('api.method', method);
    if (error.serverCode) scope.setTag('api.server_code', error.serverCode);
    if (source) scope.setTag('source', source);
    scope.setContext('api_request', {
      endpoint: error.endpoint,
      method,
      status,
      serverCode: error.serverCode,
      serverErrors: error.errors,
      ...context,
    });
    scope.setLevel(status >= 500 ? 'error' : 'warning');
    scope.setFingerprint([
      'api-error',
      String(status || 'unknown'),
      error.code,
      method,
      endpoint,
    ]);
    Sentry.captureException(error);
  });
}

/**
 * Capture payment processing errors with processor tag and structured context.
 *
 * Fingerprint uses processor + status + endpoint so Stripe/MP failures are
 * separate issues and distinct failure modes (token reuse, 500, 412) group apart.
 */
export function capturePaymentError(
  error: unknown,
  processor: 'stripe' | 'mercadopago',
  context?: Record<string, unknown>,
) {
  if (isExpectedClientTransportError(error)) return;

  const isApi = error instanceof ApiError;
  const endpoint = isApi
    ? normalizeEndpoint(error.endpoint)
    : normalizeEndpoint(context?.url as string | undefined);
  const status = isApi ? error.status ?? 0 : 0;
  const code = isApi ? error.code : 'NON_API_ERROR';
  const message = error instanceof Error ? error.message : String(error);

  const source = typeof context?.source === 'string' ? context.source : undefined;

  Sentry.withScope((scope) => {
    scope.setTag('payment.processor', processor);
    scope.setTag('api.status', String(status || 'unknown'));
    scope.setTag('api.code', code);
    scope.setTag('api.endpoint', endpoint);
    if (isApi && error.serverCode) {
      scope.setTag('api.server_code', error.serverCode);
    }
    if (source) scope.setTag('source', source);
    if (context) scope.setContext('payment', context);
    if (isApi) {
      scope.setContext('api_request', {
        endpoint: error.endpoint,
        method: error.method,
        status,
        serverCode: error.serverCode,
        serverErrors: error.errors,
      });
    }
    scope.setLevel(status >= 500 || !isApi ? 'error' : 'warning');
    scope.setFingerprint([
      'payment-error',
      processor,
      String(status || 'unknown'),
      code,
      // Normalize the backend message for grouping (strip unique tokens)
      message.replace(/tok_[A-Za-z0-9]+/g, 'tok_:id').slice(0, 120),
      endpoint,
    ]);
    Sentry.captureException(error);
  });
}

export { captureAuthError } from './authErrors';
export type { AuthErrorName, AuthErrorContext } from './authErrors';
